import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import LanguageIcon from "@/assets/icons/planetBasic/medium.svg?react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { changeAppLanguage } from "@/i18n/config"
import { useUpdatePreferredLocaleMutation } from "@/store/notificationsAPI"
import { useGetMeQuery } from "@/store/userAPI"

interface LanguageSwitcherProps {
  iconClassName?: string
  syncSession?: boolean
}

export default function LanguageSwitcher({ iconClassName, syncSession = true }: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const selectedLanguage = i18n.resolvedLanguage || i18n.language
  const preferredLocale: "en" | "es" = selectedLanguage?.startsWith("es") ? "es" : "en"
  const [updatePreferredLocale] = useUpdatePreferredLocaleMutation()
  const { data: session } = useGetMeQuery(undefined, { skip: !syncSession })

  useEffect(() => {
    if (!syncSession || !session?.success || !session.session?.tenantId) {
      return
    }

    updatePreferredLocale({ preferredLocale })
      .unwrap()
      .catch(() => undefined)
  }, [preferredLocale, syncSession, session?.success, session?.session?.tenantId, updatePreferredLocale])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger aria-label="Change language">
        <LanguageIcon className={`h-6 w-6 ${iconClassName ?? ""}`} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          className={preferredLocale === "en" ? "bg-neutral-secondBg font-medium" : ""}
          onClick={() => {
            void changeAppLanguage("en")
          }}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <span>English</span>
            {preferredLocale === "en" && <span aria-hidden="true">✓</span>}
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          className={preferredLocale === "es" ? "bg-neutral-secondBg font-medium" : ""}
          onClick={() => {
            void changeAppLanguage("es")
          }}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <span>Español</span>
            {preferredLocale === "es" && <span aria-hidden="true">✓</span>}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
